import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { adminApi } from "../../api/adminApi";
import type { FieldMapping, ReportDefinition, TransformType } from "../../api/types";
import { ReportTabs } from "../../components/ReportTabs";
import { JsonEditor } from "../../components/JsonEditor";
import { flattenSample } from "../../utils/flattenSample";

const TRANSFORMS: TransformType[] = ["NONE", "DATE_FORMAT", "CURRENCY", "UPPERCASE", "LOWERCASE"];

/** "$[0].address.city" -> "address.city", "$[0].name" -> "name", "$.length()" -> "count" -
 *  so a discovered field is immediately usable as a {{variable}} without the user having to type
 *  a name for every single one first (a blank target was previously silently dropped on save). */
function deriveTargetVariable(sourceField: string, existing: Set<string>): string {
  let candidate = sourceField
    .replace(/^\$/, "")
    .replace(/\[\d+\]/g, "")
    .replace(/\.length\(\)$/, ".count")
    .replace(/^\./, "")
    .trim();
  if (!candidate) candidate = "items";

  if (!existing.has(candidate)) return candidate;
  let i = 2;
  while (existing.has(`${candidate}${i}`)) i++;
  return `${candidate}${i}`;
}

/** The field names available on one item of a mapped list, read off the discovered leaf paths
 *  (e.g. sourceField "$" + discovered "$[0].userId" -> "userId"). Empty until the matching sample
 *  data has been discovered/fetched in this session. */
function listItemFields(sourceField: string, discovered: string[]): string[] {
  const prefix = `${sourceField}[0].`;
  return discovered.filter((d) => d.startsWith(prefix)).map((d) => d.slice(prefix.length));
}

/** A ready-to-paste Handlebars table that loops over targetVariable, one column per discovered
 *  item field - so using a mapped list means copying real, working syntax instead of guessing it. */
function buildListSnippet(targetVariable: string, fields: string[]): string {
  const cols = fields.length > 0 ? fields : ["field"];
  const headerCells = cols.map((f) => `<th>${f}</th>`).join("");
  const dataCells = cols.map((f) => `<td>{{this.${f}}}</td>`).join("\n      ");
  return `<table class="data-table">
  <thead>
    <tr>${headerCells}</tr>
  </thead>
  <tbody>
    {{#each ${targetVariable}}}
    <tr>
      ${dataCells}
    </tr>
    {{/each}}
  </tbody>
</table>`;
}

function emptyMapping(reportId: string, sortOrder: number): FieldMapping {
  return {
    reportDefinitionId: reportId,
    sourceField: "",
    targetVariable: "",
    transform: "NONE",
    transformConfig: "",
    sortOrder,
  };
}

export default function FieldMappingTable() {
  const { reportId = "" } = useParams();
  const [report, setReport] = useState<ReportDefinition | null>(null);
  const [mappings, setMappings] = useState<FieldMapping[]>([]);
  const [sampleJson, setSampleJson] = useState("{}");
  const [discovered, setDiscovered] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fetchingSample, setFetchingSample] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedFor, setCopiedFor] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([adminApi.reports.get(reportId), adminApi.reports.getMapping(reportId)])
      .then(([r, m]) => {
        setReport(r);
        setMappings(m);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load mapping"))
      .finally(() => setLoading(false));
  }, [reportId]);

  function discoverFields() {
    try {
      const parsed = JSON.parse(sampleJson);
      setDiscovered(flattenSample(parsed));
      setError(null);
    } catch (e) {
      setError(`Invalid JSON: ${(e as Error).message}`);
    }
  }

  async function fetchLiveSample() {
    setFetchingSample(true);
    setError(null);
    try {
      const data = await adminApi.reports.fetchSampleData(reportId);
      const json = JSON.stringify(data, null, 2);
      setSampleJson(json);
      setDiscovered(flattenSample(data));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch a live sample");
    } finally {
      setFetchingSample(false);
    }
  }

  function addFromDiscovered(field: string) {
    setMappings((prev) => {
      const existingTargets = new Set(prev.map((m) => m.targetVariable).filter(Boolean));
      const target = deriveTargetVariable(field, existingTargets);
      return [...prev, { ...emptyMapping(reportId, prev.length), sourceField: field, targetVariable: target }];
    });
  }

  function addBlankRow() {
    setMappings((prev) => [...prev, emptyMapping(reportId, prev.length)]);
  }

  function updateRow(index: number, patch: Partial<FieldMapping>) {
    setMappings((prev) => prev.map((m, i) => (i === index ? { ...m, ...patch } : m)));
  }

  function removeRow(index: number) {
    setMappings((prev) => prev.filter((_, i) => i !== index).map((m, i) => ({ ...m, sortOrder: i })));
  }

  async function copySnippet(targetVariable: string, snippet: string) {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopiedFor(targetVariable);
      setTimeout(() => setCopiedFor((v) => (v === targetVariable ? null : v)), 2000);
    } catch {
      setError("Couldn't copy automatically - select the snippet text and copy it manually.");
    }
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      // Drop incomplete rows silently rather than saving a broken {{}} variable that would
      // crash the designer's preview and any report using this template.
      const complete = mappings.filter((m) => m.sourceField.trim() && m.targetVariable.trim());
      const saved = await adminApi.reports.saveMapping(reportId, complete);
      setMappings(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save mapping");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p>Loading...</p>;

  return (
    <div>
      <ReportTabs reportId={reportId} title={report?.displayName ?? "Report"} />
      {error && <div className="error-text">{error}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div className="page-panel">
          <h3>Discover fields from sample data</h3>
          <p className="help-text">
            Paste a sample response from your data source (or the payload you'll push into{" "}
            <code>data</code>), or fetch one live from the endpoint configured in Data source.
            Works for a flat object, a nested object, or an array. An array shows up twice below:
            once as the <strong>whole list</strong> (map that to loop over it with{" "}
            <code>{"{{#each yourVariable}}"}</code> in the template — use "Insert list table" in
            the Designer's snippet panel), and once per field sampled from its first item (map one
            of those to pull a single value out).
          </p>
          <JsonEditor value={sampleJson} onChange={setSampleJson} rows={10} />
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button type="button" className="btn-secondary" onClick={discoverFields}>
              Discover fields
            </button>
            <button type="button" className="btn-secondary" onClick={fetchLiveSample} disabled={fetchingSample}>
              {fetchingSample ? "Fetching..." : "Fetch live sample"}
            </button>
          </div>

          {discovered.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <h4>Discovered fields</h4>
              {discovered.map((f) => {
                // A field is "the whole list" if some other discovered path is one of its items
                // (e.g. "$" is the list when "$[0].userId" is also discovered) - flattenSample
                // emits both so a repeating table can be built, not just single-value fields.
                const isList = discovered.some((other) => other !== f && other.startsWith(`${f}[`));
                return (
                  <button
                    key={f}
                    type="button"
                    className="variable-chip"
                    onClick={() => addFromDiscovered(f)}
                    title={isList ? "Add as mapping row - loop over this with {{#each}}" : "Add as mapping row"}
                  >
                    {f}
                    {isList && " (list)"}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="page-panel">
          <h3>Field mapping</h3>
          <p className="help-text">Map each source field to the template's {"{{variable}}"} name.</p>

          {mappings.map((m, index) => (
            <div
              key={index}
              style={{ border: "1px solid var(--color-border)", borderRadius: 6, padding: 10, marginBottom: 8 }}
            >
              <div className="form-row">
                <label>Source field (JSONPath)</label>
                <input value={m.sourceField} onChange={(e) => updateRow(index, { sourceField: e.target.value })} />
                {m.sourceField && !m.sourceField.startsWith("$") && (
                  <p className="error-text" style={{ margin: "4px 0 0" }}>
                    Doesn't look like JSONPath (should start with "$") - this won't match
                    anything. Use a chip from "Discovered fields" on the left instead of typing
                    one by hand.
                  </p>
                )}
              </div>
              <div className="form-row">
                <label>Target variable</label>
                <input
                  value={m.targetVariable}
                  onChange={(e) => updateRow(index, { targetVariable: e.target.value })}
                  placeholder="member.balance"
                />
              </div>
              <div className="form-row">
                <label>Transform</label>
                <select
                  value={m.transform}
                  onChange={(e) => updateRow(index, { transform: e.target.value as TransformType })}
                >
                  {TRANSFORMS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              {m.transform !== "NONE" && (
                <div className="form-row">
                  <label>Transform config</label>
                  <input
                    value={m.transformConfig ?? ""}
                    onChange={(e) => updateRow(index, { transformConfig: e.target.value })}
                    placeholder={m.transform === "DATE_FORMAT" ? "yyyy-MM-dd" : ""}
                  />
                </div>
              )}
              <button type="button" className="btn-danger" onClick={() => removeRow(index)}>
                Remove
              </button>
            </div>
          ))}

          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button type="button" className="btn-secondary" onClick={addBlankRow}>
              Add row
            </button>
            <button type="button" className="btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save mapping"}
            </button>
          </div>
        </div>
      </div>

      {mappings.some((m) => listItemFields(m.sourceField, discovered).length > 0) && (
        <div className="page-panel" style={{ marginTop: 16 }}>
          <h3>Ready-to-paste list template</h3>
          <p className="help-text">
            You've mapped a whole array to a variable below — here's a working{" "}
            <code>{"{{#each}}"}</code> table built from the fields on its first item. Copy it
            straight into the HTML template on the Designer tab, then trim the columns you don't
            need.
          </p>
          {mappings
            .filter((m) => listItemFields(m.sourceField, discovered).length > 0)
            .map((m) => {
              const fields = listItemFields(m.sourceField, discovered);
              const snippet = buildListSnippet(m.targetVariable, fields);
              return (
                <div key={m.targetVariable} style={{ marginBottom: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <strong>
                      {m.sourceField} → {"{{#each "}
                      {m.targetVariable}
                      {"}}"}
                    </strong>
                    <button type="button" className="btn-secondary" onClick={() => copySnippet(m.targetVariable, snippet)}>
                      {copiedFor === m.targetVariable ? "Copied!" : "Copy snippet"}
                    </button>
                  </div>
                  <pre className="code-snippet-preview">{snippet}</pre>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
}
